import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Target, Trophy, TrendingUp, Flame, GraduationCap, 
  Eye, Heart, Dumbbell, Brain, CheckCircle, Circle,
  ChevronRight, Zap, Star, Award, Users, BarChart3,
  Calendar, Clock, ArrowUp, ArrowDown, Minus
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Score tier definitions
const SCORE_TIERS = [
  { min: 90, label: 'Elite', color: 'text-yellow-400', bg: 'bg-yellow-500/20', desc: 'Top 5% - Ready for any opportunity' },
  { min: 80, label: 'Excellent', color: 'text-green-400', bg: 'bg-green-500/20', desc: 'Top 15% - Highly competitive' },
  { min: 70, label: 'Strong', color: 'text-blue-400', bg: 'bg-blue-500/20', desc: 'Top 30% - Well-positioned' },
  { min: 60, label: 'Developing', color: 'text-orange-400', bg: 'bg-orange-500/20', desc: 'On track - Keep building' },
  { min: 0, label: 'Building', color: 'text-gray-400', bg: 'bg-gray-500/20', desc: 'Foundation phase - Room to grow' },
];

const getScoreTier = (score) => {
  return SCORE_TIERS.find(tier => score >= tier.min) || SCORE_TIERS[SCORE_TIERS.length - 1];
};

const AthleteReadinessPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [readinessData, setReadinessData] = useState(null);
  const [activeCategory, setActiveCategory] = useState('overview');

  useEffect(() => {
    fetchReadinessScore();
  }, []);

  const fetchReadinessScore = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/athlete/readiness-score`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReadinessData(response.data);
    } catch (error) {
      console.error('Failed to fetch readiness score:', error);
      // Use mock data for display
      setReadinessData({
        overall_score: 72,
        categories: {
          performance: { score: 75, max: 40, weighted: 30, trend: 'up' },
          consistency: { score: 80, max: 25, weighted: 20, trend: 'up' },
          academics: { score: 70, max: 15, weighted: 10.5, trend: 'stable' },
          visibility: { score: 60, max: 10, weighted: 6, trend: 'up' },
          character: { score: 55, max: 10, weighted: 5.5, trend: 'stable' },
        },
        breakdown: {
          performance: {
            items: [
              { name: 'Workout Completion Rate', value: 85, target: 90, unit: '%' },
              { name: 'Combine Metrics Logged', value: 4, target: 6, unit: 'metrics' },
              { name: 'Week-over-Week Improvement', value: 12, target: 10, unit: '%' },
            ]
          },
          consistency: {
            items: [
              { name: 'Current Training Streak', value: 7, target: 14, unit: 'days' },
              { name: 'Daily Challenge Completion', value: 80, target: 85, unit: '%' },
              { name: 'Recovery Log Frequency', value: 5, target: 7, unit: 'days/week' },
            ]
          },
          academics: {
            items: [
              { name: 'GPA', value: 3.2, target: 3.5, unit: '' },
              { name: 'Eligibility Status', value: 100, target: 100, unit: '%', isBoolean: true },
            ]
          },
          visibility: {
            items: [
              { name: 'Profile Completeness', value: 70, target: 100, unit: '%' },
              { name: 'Highlight Reel Uploaded', value: 0, target: 1, unit: '', isBoolean: true },
              { name: 'Social Links Added', value: 1, target: 3, unit: 'links' },
            ]
          },
          character: {
            items: [
              { name: 'Leadership Activities', value: 1, target: 2, unit: 'activities' },
              { name: 'Community Service Hours', value: 5, target: 10, unit: 'hours' },
            ]
          }
        },
        recommendations: [
          { category: 'performance', action: 'Log 2 more combine metrics to boost your Performance score', priority: 'high' },
          { category: 'visibility', action: 'Upload a highlight reel to increase visibility to scouts', priority: 'high' },
          { category: 'consistency', action: 'Extend your streak to 14 days for a consistency bonus', priority: 'medium' },
          { category: 'character', action: 'Add community service hours to show character', priority: 'low' },
        ],
        history: [
          { date: '2026-03-08', score: 68 },
          { date: '2026-03-09', score: 69 },
          { date: '2026-03-10', score: 70 },
          { date: '2026-03-11', score: 70 },
          { date: '2026-03-12', score: 71 },
          { date: '2026-03-13', score: 72 },
          { date: '2026-03-14', score: 72 },
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'performance': return Dumbbell;
      case 'consistency': return Flame;
      case 'academics': return GraduationCap;
      case 'visibility': return Eye;
      case 'character': return Heart;
      default: return Target;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'performance': return 'text-aura-primary bg-aura-primary/20';
      case 'consistency': return 'text-orange-400 bg-orange-500/20';
      case 'academics': return 'text-blue-400 bg-blue-500/20';
      case 'visibility': return 'text-purple-400 bg-purple-500/20';
      case 'character': return 'text-pink-400 bg-pink-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <ArrowUp className="w-4 h-4 text-green-400" />;
      case 'down': return <ArrowDown className="w-4 h-4 text-red-400" />;
      default: return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-12 h-12 border-4 border-aura-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const tier = getScoreTier(readinessData?.overall_score || 0);
  const categories = readinessData?.categories || {};

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white uppercase tracking-tight">
              Athlete Readiness
            </h1>
            <p className="text-aura-text-muted mt-1">Your foundation for NIL, transfers, and recruitment success</p>
          </div>
          <Badge className={`${tier.bg} ${tier.color} border-0 text-sm px-4 py-2`}>
            <Star className="w-4 h-4 mr-2" />
            {tier.label} Tier
          </Badge>
        </div>

        {/* Main Score Card */}
        <Card className="bg-gradient-to-br from-aura-primary/20 via-aura-surface to-aura-secondary/10 border-aura-primary/30 overflow-hidden">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col lg:flex-row items-center gap-8">
              {/* Score Circle */}
              <div className="relative">
                <svg className="w-48 h-48 transform -rotate-90">
                  <circle
                    cx="96"
                    cy="96"
                    r="88"
                    stroke="currentColor"
                    strokeWidth="12"
                    fill="none"
                    className="text-white/10"
                  />
                  <circle
                    cx="96"
                    cy="96"
                    r="88"
                    stroke="currentColor"
                    strokeWidth="12"
                    fill="none"
                    strokeDasharray={553}
                    strokeDashoffset={553 - (553 * (readinessData?.overall_score || 0)) / 100}
                    strokeLinecap="round"
                    className={tier.color}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`font-heading font-bold text-5xl ${tier.color}`}>
                    {readinessData?.overall_score || 0}
                  </span>
                  <span className="text-sm text-aura-text-muted uppercase tracking-wider">Readiness</span>
                </div>
              </div>

              {/* Score Breakdown */}
              <div className="flex-1 w-full">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Object.entries(categories).map(([key, data]) => {
                    const Icon = getCategoryIcon(key);
                    const colorClass = getCategoryColor(key);
                    return (
                      <div 
                        key={key}
                        className="flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                        onClick={() => setActiveCategory(key)}
                        data-testid={`category-${key}`}
                      >
                        <div className={`w-10 h-10 rounded-lg ${colorClass.split(' ')[1]} flex items-center justify-center`}>
                          <Icon className={`w-5 h-5 ${colorClass.split(' ')[0]}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-white capitalize">{key}</p>
                            <div className="flex items-center gap-1">
                              <span className={`font-bold ${colorClass.split(' ')[0]}`}>{data.score}</span>
                              {getTrendIcon(data.trend)}
                            </div>
                          </div>
                          <Progress value={data.score} className="h-1.5 mt-1" />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Tier Description */}
                <div className="mt-4 p-3 bg-white/5 rounded-lg">
                  <p className="text-sm text-aura-text-muted">
                    <span className={`font-bold ${tier.color}`}>{tier.label}:</span> {tier.desc}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* What This Score Means */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-aura-surface border-white/10 hover:border-green-500/30 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="font-heading font-bold text-white uppercase text-sm">NIL Ready</p>
                  <p className="text-xs text-aura-text-muted">Brand partnership potential</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-green-400">
                  {readinessData?.overall_score >= 70 ? 'Yes' : 'Building'}
                </span>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="text-green-400"
                  onClick={() => navigate('/nil-dashboard')}
                >
                  View <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-aura-surface border-white/10 hover:border-blue-500/30 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="font-heading font-bold text-white uppercase text-sm">Transfer Ready</p>
                  <p className="text-xs text-aura-text-muted">Portal competitiveness</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-blue-400">
                  {readinessData?.overall_score >= 75 ? 'High' : readinessData?.overall_score >= 60 ? 'Medium' : 'Low'}
                </span>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="text-blue-400"
                  onClick={() => navigate('/transfer-portal')}
                >
                  View <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-aura-surface border-white/10 hover:border-purple-500/30 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="font-heading font-bold text-white uppercase text-sm">Recruit Rating</p>
                  <p className="text-xs text-aura-text-muted">Scout attractiveness</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map(star => (
                    <Star 
                      key={star}
                      className={`w-5 h-5 ${
                        star <= Math.round(readinessData?.overall_score / 20) 
                          ? 'text-yellow-400 fill-yellow-400' 
                          : 'text-gray-600'
                      }`}
                    />
                  ))}
                </div>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="text-purple-400"
                  onClick={() => navigate('/my-profile')}
                >
                  View <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Category Tabs */}
        <Tabs value={activeCategory} onValueChange={setActiveCategory}>
          <TabsList className="w-full bg-aura-surface border border-white/10 flex-wrap h-auto p-1">
            <TabsTrigger value="overview" className="flex-1 data-[state=active]:bg-aura-primary">
              Overview
            </TabsTrigger>
            <TabsTrigger value="performance" className="flex-1 data-[state=active]:bg-aura-primary">
              Performance
            </TabsTrigger>
            <TabsTrigger value="consistency" className="flex-1 data-[state=active]:bg-aura-primary">
              Consistency
            </TabsTrigger>
            <TabsTrigger value="academics" className="flex-1 data-[state=active]:bg-aura-primary">
              Academics
            </TabsTrigger>
            <TabsTrigger value="visibility" className="flex-1 data-[state=active]:bg-aura-primary">
              Visibility
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  Priority Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {readinessData?.recommendations?.map((rec, idx) => {
                  const Icon = getCategoryIcon(rec.category);
                  const priorityColor = rec.priority === 'high' ? 'border-red-500/50 bg-red-500/10' :
                                       rec.priority === 'medium' ? 'border-yellow-500/50 bg-yellow-500/10' :
                                       'border-green-500/50 bg-green-500/10';
                  return (
                    <div 
                      key={idx}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${priorityColor}`}
                    >
                      <Icon className="w-5 h-5 text-aura-text-muted" />
                      <p className="flex-1 text-white text-sm">{rec.action}</p>
                      <Badge className={`capitalize border-0 ${
                        rec.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                        rec.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-green-500/20 text-green-400'
                      }`}>
                        {rec.priority}
                      </Badge>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Score History */}
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-aura-secondary" />
                  7-Day Progress
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end justify-between h-32 gap-2">
                  {readinessData?.history?.map((day, idx) => {
                    const height = (day.score / 100) * 100;
                    const isToday = idx === readinessData.history.length - 1;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                        <span className="text-xs text-aura-text-muted">{day.score}</span>
                        <div 
                          className={`w-full rounded-t-md transition-all ${
                            isToday ? 'bg-aura-primary' : 'bg-white/20'
                          }`}
                          style={{ height: `${height}%` }}
                        />
                        <span className="text-xs text-aura-text-subtle">
                          {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Category Detail Tabs */}
          {['performance', 'consistency', 'academics', 'visibility', 'character'].map(category => (
            <TabsContent key={category} value={category} className="space-y-4 mt-4">
              <Card className="bg-aura-surface border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                      {React.createElement(getCategoryIcon(category), { className: `w-5 h-5 ${getCategoryColor(category).split(' ')[0]}` })}
                      {category} Score
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <span className={`text-2xl font-bold ${getCategoryColor(category).split(' ')[0]}`}>
                        {categories[category]?.score || 0}
                      </span>
                      <span className="text-aura-text-muted">/100</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {readinessData?.breakdown?.[category]?.items?.map((item, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white">{item.name}</span>
                        <span className="text-sm">
                          <span className={item.value >= item.target ? 'text-green-400' : 'text-yellow-400'}>
                            {item.value}
                          </span>
                          <span className="text-aura-text-muted">/{item.target} {item.unit}</span>
                        </span>
                      </div>
                      <Progress 
                        value={Math.min((item.value / item.target) * 100, 100)} 
                        className="h-2"
                      />
                    </div>
                  ))}

                  {/* Category-specific tips */}
                  <div className="mt-4 p-3 bg-white/5 rounded-lg">
                    <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-2">
                      How to improve {category}
                    </p>
                    <p className="text-sm text-white">
                      {category === 'performance' && 'Complete more workouts and log your combine metrics regularly.'}
                      {category === 'consistency' && 'Maintain your training streak and complete daily challenges.'}
                      {category === 'academics' && 'Keep your GPA up and maintain eligibility status.'}
                      {category === 'visibility' && 'Complete your profile, upload highlights, and add social links.'}
                      {category === 'character' && 'Log leadership activities and community service hours.'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>

        {/* Bottom CTA */}
        <Card className="bg-gradient-to-r from-aura-secondary/20 to-aura-primary/20 border-aura-secondary/30">
          <CardContent className="p-6 text-center">
            <h3 className="font-heading font-bold text-xl text-white uppercase">
              Build Your Foundation Daily
            </h3>
            <p className="text-aura-text-muted mt-2 max-w-lg mx-auto">
              Every workout, every meal logged, every day of consistency adds to your Readiness Score. 
              The higher your score, the more opportunities open up.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-4">
              <Button onClick={() => navigate('/training-program')} className="bg-aura-primary hover:bg-aura-primary-hover">
                <Dumbbell className="w-4 h-4 mr-2" />
                Log Workout
              </Button>
              <Button onClick={() => navigate('/my-profile')} variant="outline" className="border-white/20 text-white hover:bg-white/10">
                <Eye className="w-4 h-4 mr-2" />
                Complete Profile
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AthleteReadinessPage;
